import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  Slider,
  FormControlLabel,
  Switch,
  Divider,
  CircularProgress,
  Alert,
  Fade,
  Zoom,
  Tooltip,
  IconButton,
  useTheme,
  Card,
  CardContent,
  Collapse,
  Stack,
  Avatar,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PsychologyIcon from '@mui/icons-material/Psychology';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import TuneIcon from '@mui/icons-material/Tune';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import LanguageIcon from '@mui/icons-material/Language';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import MicIcon from '@mui/icons-material/Mic';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import StopIcon from '@mui/icons-material/Stop'; // For stopping recording
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'; // For indicating recording
import DeleteIcon from '@mui/icons-material/Delete'; // For clearing recording/file
import { useAppContext } from '../../context/AppContext';
import { queryApi } from '../../services/api';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

// Sample questions for inspiration
const SAMPLE_QUESTIONS = [
  "What are the risk factors for neonatal jaundice?",
  "How is respiratory distress syndrome diagnosed in premature infants?",
  "What are the current treatment options for necrotizing enterocolitis?",
  "What are the long-term outcomes of hypoxic-ischemic encephalopathy?",
  "How does maternal diabetes affect neonatal outcomes?",
];

const QueryForm = ({ onResultsReceived }) => {
  const theme = useTheme();
  const { topics, ragStrategies, selectedStrategy, setSelectedStrategy } = useAppContext();

  const [query, setQuery] = useState('');
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [topK, setTopK] = useState(5);
  const [useReasoning, setUseReasoning] = useState(true);
  const [useWebSearch, setUseWebSearch] = useState(false);
  const [useDirect, setUseDirect] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [audioFile, setAudioFile] = useState(null); // Stores uploaded or recorded File object
  const audioInputRef = React.useRef(null);
  const mediaRecorderRef = React.useRef(null);
  const audioUrlRef = React.useRef(null); // Stores the current object URL string for cleanup

  // States for recording
  const [isRecording, setIsRecording] = useState(false);
  // audioChunks is now a local variable within startRecording
  const [audioUrl, setAudioUrl] = useState(null); // For <audio> src preview
  const [recordingError, setRecordingError] = useState(null);

  // Effect for component mount and unmount
  useEffect(() => {
    setMounted(true);
    return () => { // Component unmount cleanup
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }
      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current);
        audioUrlRef.current = null;
      }
    };
  }, []); // Empty dependency array: runs only on mount and unmount

  // Effect to manage audioUrlRef when audioUrl state changes for cleanup
  useEffect(() => {
    if (audioUrl) {
      // If a previous URL existed in the ref and it's different from the new one, revoke it.
      if (audioUrlRef.current && audioUrlRef.current !== audioUrl) {
        URL.revokeObjectURL(audioUrlRef.current);
      }
      audioUrlRef.current = audioUrl; // Store the new URL in the ref.
    } else {
      // If audioUrl became null (e.g. audio cleared), and there was a URL in ref, revoke it.
      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current);
        audioUrlRef.current = null;
      }
    }
  }, [audioUrl]); // Runs when audioUrl changes

  // New useEffect to trace audioFile state changes
  useEffect(() => {
    console.log('[QueryForm DEBUG useEffect] audioFile state changed:', audioFile);
    if (audioFile) {
      console.log('[QueryForm DEBUG useEffect] new audioFile details: name=', audioFile.name, 'size=', audioFile.size, 'type=', audioFile.type);
    }
  }, [audioFile]); // Dependency on audioFile


  const handleAudioFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      clearRecordingInProgress(); // Stop any active recording
      setAudioFile(file); // file is already a File object
      const newUrl = URL.createObjectURL(file);
      setAudioUrl(newUrl);
      setRecordingError(null); // Clear recording errors if a file is successfully uploaded
    }
  };
  
  // Stops recording and resets recording-specific states, but doesn't clear the resulting file/URL
  const clearRecordingInProgress = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop(); // This will trigger onstop if recording
    }
    setIsRecording(false); // Ensure UI reflects recording stopped
    // mediaRecorderRef.current is nullified in onstop or onerror
    // setAudioChunks([]); // audioChunks is now local to startRecording
  };

  const clearAudio = () => {
    console.log('[QueryForm DEBUG] clearAudio called. Current audioFile state before clearing:', audioFile);
    clearRecordingInProgress(); // Stop recording if active
    setAudioFile(null);
    setAudioUrl(null); // This will trigger useEffect to revoke via audioUrlRef
    setRecordingError(null);
    if (audioInputRef.current) {
      audioInputRef.current.value = ""; // Reset file input
    }
  };

  const startRecording = async () => {
    clearAudio(); // Clear any previous audio (file or recording) first
    setRecordingError(null); // Clear previous errors

    if (!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)) {
      setRecordingError("Audio recording is not supported by your browser.");
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      const options = {};
      // Prefer webm with opus if available, good quality and compression
      if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
        options.mimeType = 'audio/webm;codecs=opus';
      } else if (MediaRecorder.isTypeSupported('audio/webm')) {
        options.mimeType = 'audio/webm';
      } else if (MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')) {
        options.mimeType = 'audio/ogg;codecs=opus';
      } else if (MediaRecorder.isTypeSupported('audio/mp4')) { // MP4 might be more widely supported on Apple devices
        options.mimeType = 'audio/mp4';
      }
      // If none of the preferred are supported, the browser will use its default.
      
      const recorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = recorder;
      let localAudioChunks = []; // Use local variable to collect chunks

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          localAudioChunks.push(event.data);
        }
      };

      recorder.onstop = () => {
        const mimeType = mediaRecorderRef.current?.mimeType || 'audio/webm'; // Fallback, though recorder.mimeType should be set
        const audioBlob = new Blob(localAudioChunks, { type: mimeType });
        console.log('[QueryForm DEBUG] recorder.onstop: audioBlob created, size:', audioBlob.size, 'type:', audioBlob.type);

        if (audioBlob.size === 0) {
          console.error("[QueryForm DEBUG] recorder.onstop: Recorded audio blob is empty!");
          setRecordingError("Recording resulted in an empty audio file. Please try again.");
        } else {
          const newUrl = URL.createObjectURL(audioBlob);
          setAudioUrl(newUrl); // This will trigger the useEffect for audioUrl
          
          let fileExtension = 'webm'; // Default extension
          if (mimeType.includes('webm')) fileExtension = 'webm';
          else if (mimeType.includes('ogg')) fileExtension = 'ogg';
          else if (mimeType.includes('mp4')) fileExtension = 'm4a';
          else if (mimeType.includes('wav')) fileExtension = 'wav';
          
          const filename = `recorded_audio.${fileExtension}`;
          const newAudioFile = new File([audioBlob], filename, { type: mimeType });
          console.log('[QueryForm DEBUG] recorder.onstop: newAudioFile created:', newAudioFile);
          setAudioFile(newAudioFile); // This will trigger the useEffect for audioFile
        }
        
        localAudioChunks = []; // Clear for next recording
        stream.getTracks().forEach(track => track.stop()); // Stop microphone tracks
        mediaRecorderRef.current = null; // Clear the ref once stopped and processed
        setIsRecording(false); // Ensure UI updates after stopping
      };
      
      recorder.onerror = (event) => {
        console.error("MediaRecorder error:", event.error);
        setRecordingError(`Recording error: ${event.error.name || 'Unknown error'}`);
        setIsRecording(false);
        stream.getTracks().forEach(track => track.stop());
        mediaRecorderRef.current = null;
      };

      recorder.start(); // Start recording
      setIsRecording(true);
    } catch (err) {
      console.error("Error accessing microphone:", err);
      setRecordingError(`Could not access microphone: ${err.message}. Please ensure permission is granted.`);
      setIsRecording(false); // Ensure UI reflects that recording didn't start
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.stop();
      // setIsRecording(false); // This is now handled in onstop/onerror to ensure it's set after processing
    }
  };

  const handleTopicsChange = (event) => {
    const {
      target: { value },
    } = event;
    setSelectedTopics(
      // On autofill we get a stringified value.
      typeof value === 'string' ? value.split(',') : value,
    );
  };

  const handleStrategyChange = (event) => {
    setSelectedStrategy(event.target.value);
  };

  const handleTopKChange = (event, newValue) => {
    setTopK(newValue);
  };

  const handleSampleQuestionClick = (question) => {
    setQuery(question);
    setShowSuggestions(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    console.log('[QueryForm DEBUG] handleSubmit triggered.');
    console.log('[QueryForm DEBUG] Current query state:', query);
    console.log('[QueryForm DEBUG] Current audioFile state:', audioFile);
    if (audioFile) {
      console.log('[QueryForm DEBUG] audioFile details: name=', audioFile.name, 'size=', audioFile.size, 'type=', audioFile.type);
    }


    if (!query.trim() && !audioFile) {
      setError('Please enter a query or select an audio file.');
      console.log('[QueryForm DEBUG] Validation failed: No query text and no audio file.');
      return;
    }

    setLoading(true);
    setError(null);

    // Always use FormData for /query_flexible_tool as it now expects form fields
    const formData = new FormData();
    if (query.trim()) {
      formData.append('query_text', query.trim());
      console.log('[QueryForm DEBUG] Appended query_text to FormData:', query.trim());
    }
    
    // Critical check for audioFile right before appending
    console.log('[QueryForm DEBUG] Just before appending audio_file, its value is:', audioFile);
    if (audioFile && audioFile instanceof File) { // Ensure it's a File object
      formData.append('audio_file', audioFile, audioFile.name);
      console.log('[QueryForm DEBUG] Appended audio_file to FormData:', audioFile.name);
    } else {
      console.log('[QueryForm DEBUG] audio_file was NOT appended. Value:', audioFile);
    }

    if (selectedTopics.length > 0) {
      formData.append('topic_ids_str', JSON.stringify(selectedTopics));
    } else {
      // Ensure backend handles null or empty string for topic_ids_str appropriately if no topics selected
      // Or omit if backend defaults correctly:
      // formData.append('topic_ids_str', JSON.stringify([])); // or null, check backend
    }
    formData.append('top_k', topK);
    if (selectedStrategy) {
      formData.append('rag_strategy', selectedStrategy);
    }
    formData.append('use_reasoning', useReasoning);
    formData.append('use_rag', true); // RAG is the primary function
    formData.append('use_websearch', useWebSearch);
    formData.append('use_direct', useDirect);

    try {
      // The /query_flexible_tool endpoint is now the primary one handling form data
      // and audio. Other queryApi methods might need similar FormData adaptation
      // if they were to support audio, or be deprecated in favor of flexible_tool.
      const response = await queryApi.queryFlexibleTool(formData);
      
      onResultsReceived(response, useReasoning); // Assuming response structure is compatible
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to execute query. Please try again.');
      onResultsReceived(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Fade in={mounted} timeout={800}>
      <Paper
        elevation={3}
        sx={{
          p: { xs: 3, md: 4 },
          mb: 4,
          borderRadius: 3,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Decorative background element */}
        <Box
          sx={{
            position: 'absolute',
            top: -60,
            right: -60,
            width: 120,
            height: 120,
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${theme.palette.primary.light}20, ${theme.palette.primary.main}10)`,
            zIndex: 0,
          }}
        />

        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Avatar
              sx={{
                bgcolor: 'primary.main',
                mr: 2,
                width: 48,
                height: 48,
                boxShadow: 2,
              }}
            >
              <SearchIcon />
            </Avatar>
            <Typography variant="h5" component="h2" fontWeight="bold">
              Query Neonatology Knowledge Base
            </Typography>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, ml: 9 }}>
            Ask questions about neonatology research and get answers based on the available papers
          </Typography>

          <Divider sx={{ mb: 4 }} />

          {error && (
            <Zoom in={true}>
              <Alert
                severity="error"
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  boxShadow: 1,
                }}
              >
                {error}
              </Alert>
            </Zoom>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Box sx={{ position: 'relative' }}>
              <TextField
                fullWidth
                label="Enter your question"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                required
                multiline
                rows={3}
                placeholder="e.g., What are the risk factors for neonatal jaundice?"
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    transition: 'all 0.3s ease',
                    '&.Mui-focused': {
                      boxShadow: '0 0 0 3px rgba(63, 81, 181, 0.2)',
                    },
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <Tooltip title="Get question suggestions">
                      <IconButton
                        onClick={() => setShowSuggestions(!showSuggestions)}
                        color={showSuggestions ? "primary" : "default"}
                        sx={{
                          position: 'absolute',
                          bottom: 8,
                          right: 8,
                        }}
                      >
                        <LightbulbIcon />
                      </IconButton>
                    </Tooltip>
                  ),
                }}
              />

              <Collapse in={showSuggestions}>
                <Card
                  variant="outlined"
                  sx={{
                    mb: 3,
                    borderRadius: 2,
                    borderColor: 'primary.light',
                    bgcolor: 'background.paper',
                  }}
                >
                  <CardContent sx={{ py: 2 }}>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      Sample Questions for Inspiration
                    </Typography>
                    <Stack spacing={1}>
                      {SAMPLE_QUESTIONS.map((question, index) => (
                        <Chip
                          key={index}
                          label={question}
                          onClick={() => handleSampleQuestionClick(question)}
                          clickable
                          color="primary"
                          variant="outlined"
                          sx={{
                            borderRadius: 2,
                            py: 2.5,
                            justifyContent: 'flex-start',
                            '& .MuiChip-label': {
                              px: 1,
                              fontSize: '0.9rem',
                            },
                            '&:hover': {
                              bgcolor: 'primary.light',
                              color: 'primary.contrastText',
                              borderColor: 'primary.light',
                            },
                            transition: 'all 0.2s ease',
                          }}
                        />
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              </Collapse>
            </Box>
            
            {/* Audio Input Section */}
            <Box sx={{ mb: 3, p: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Audio Query</Typography>
              {recordingError && <Alert severity="error" sx={{ mb: 1 }}>{recordingError}</Alert>}
              <Stack direction="row" spacing={2} alignItems="center">
                <Button
                  variant="outlined"
                  onClick={isRecording ? stopRecording : startRecording}
                  startIcon={isRecording ? <StopIcon /> : <MicIcon />}
                  color={isRecording ? "error" : "primary"}
                  sx={{ borderRadius: 2 }}
                >
                  {isRecording ? 'Stop Recording' : 'Start Recording'}
                </Button>
                <Typography variant="caption" sx={{ fontStyle: 'italic' }}>OR</Typography>
                <Button
                  variant="outlined"
                  onClick={() => audioInputRef.current && audioInputRef.current.click()}
                  startIcon={<AttachFileIcon />}
                  sx={{ borderRadius: 2 }}
                  disabled={isRecording}
                >
                  Upload Audio File
                </Button>
                <input
                  type="file"
                  accept="audio/*"
                  onChange={handleAudioFileChange}
                  style={{ display: 'none' }}
                  ref={audioInputRef}
                  id="audio-upload-input"
                />
              </Stack>

              {isRecording && (
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1, color: 'error.main' }}>
                  <FiberManualRecordIcon fontSize="small" />
                  <Typography variant="body2">Recording...</Typography>
                </Stack>
              )}

              {audioUrl && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption">Preview:</Typography>
                  <audio src={audioUrl} controls style={{ width: '100%', marginTop: '4px' }} />
                </Box>
              )}
              
              {(audioFile || audioUrl) && !isRecording && (
                 <Button
                    variant="text"
                    color="warning"
                    onClick={clearAudio}
                    startIcon={<DeleteIcon />}
                    size="small"
                    sx={{ mt: 1, borderRadius: 2 }}
                  >
                    Clear Audio
                  </Button>
              )}
            </Box>
            
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={useReasoning}
                    onChange={(e) => setUseReasoning(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <PsychologyIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography>Use reasoning loop</Typography>
                    <Tooltip
                      title="The reasoning loop evaluates retrieved information and may perform additional searches to improve answer quality."
                      arrow
                    >
                      <IconButton size="small" sx={{ ml: 0.5 }}>
                        <HelpOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                }
              />

              <Button
                variant="text"
                color="primary"
                onClick={() => setShowAdvanced(!showAdvanced)}
                endIcon={showAdvanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                startIcon={<TuneIcon />}
                sx={{
                  borderRadius: 8,
                  '&:hover': {
                    bgcolor: 'rgba(63, 81, 181, 0.08)',
                  },
                }}
              >
                Advanced Options
              </Button>
            </Box>

            <Collapse in={showAdvanced}>
              <Card
                variant="outlined"
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  borderColor: theme.palette.divider,
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, mb: 3 }}>
                    <FormControl sx={{ flex: 1, minWidth: 200 }}>
                      <InputLabel id="topics-select-label">Filter by Topics (Optional)</InputLabel>
                      <Select
                        labelId="topics-select-label"
                        multiple
                        value={selectedTopics}
                        onChange={handleTopicsChange}
                        input={<OutlinedInput label="Filter by Topics (Optional)" />}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((value) => (
                              <Chip
                                key={value}
                                label={value}
                                size="small"
                                sx={{ borderRadius: 1 }}
                              />
                            ))}
                          </Box>
                        )}
                        MenuProps={MenuProps}
                        sx={{ borderRadius: 2 }}
                      >
                        {topics.map((topic) => (
                          <MenuItem key={topic} value={topic}>
                            {topic}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl sx={{ flex: 1, minWidth: 200 }}>
                      <InputLabel id="rag-strategy-label">RAG Strategy</InputLabel>
                      <Select
                        labelId="rag-strategy-label"
                        value={selectedStrategy}
                        label="RAG Strategy"
                        onChange={handleStrategyChange}
                        sx={{ borderRadius: 2 }}
                      >
                        {ragStrategies.map((strategy) => (
                          <MenuItem key={strategy.id} value={strategy.id}>
                            {strategy.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  <Box sx={{ px: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography id="top-k-slider" variant="body2" color="text.secondary">
                        Number of chunks to retrieve (top-k)
                      </Typography>
                      <Chip
                        label={topK}
                        color="primary"
                        size="small"
                        sx={{
                          fontWeight: 'bold',
                          minWidth: 32,
                          height: 24,
                        }}
                      />
                    </Box>
                    <Slider
                      value={topK}
                      onChange={handleTopKChange}
                      aria-labelledby="top-k-slider"
                      valueLabelDisplay="auto"
                      step={1}
                      marks
                      min={1}
                      max={10}
                      sx={{
                        color: 'primary.main',
                        '& .MuiSlider-thumb': {
                          '&:hover, &.Mui-focusVisible': {
                            boxShadow: `0px 0px 0px 8px ${theme.palette.primary.main}20`,
                          },
                        },
                      }}
                    />
                  </Box>

                  <Divider sx={{ my: 3 }} />
                  
                  <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                    Knowledge Sources
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={true}
                          disabled
                          color="primary"
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <IntegrationInstructionsIcon sx={{ mr: 1, color: 'primary.main' }} />
                          <Typography>Use RAG (Knowledge Base)</Typography>
                          <Tooltip
                            title="Search through the neonatology knowledge base (always enabled)"
                            arrow
                          >
                            <IconButton size="small" sx={{ ml: 0.5 }}>
                              <HelpOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      }
                    />
                    
                    <FormControlLabel
                      control={
                        <Switch
                          checked={useWebSearch}
                          onChange={(e) => setUseWebSearch(e.target.checked)}
                          color="primary"
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <LanguageIcon sx={{ mr: 1, color: 'info.main' }} />
                          <Typography>Use Web Search</Typography>
                          <Tooltip
                            title="Search the web for additional information using DuckDuckGo"
                            arrow
                          >
                            <IconButton size="small" sx={{ ml: 0.5 }}>
                              <HelpOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      }
                    />
                    
                    <FormControlLabel
                      control={
                        <Switch
                          checked={useDirect}
                          onChange={(e) => setUseDirect(e.target.checked)}
                          color="primary"
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <AutoAwesomeIcon sx={{ mr: 1, color: 'warning.main' }} />
                          <Typography>Use Direct LLM Knowledge</Typography>
                          <Tooltip
                            title="Allow the LLM to answer directly from its training knowledge when appropriate"
                            arrow
                          >
                            <IconButton size="small" sx={{ ml: 0.5 }}>
                              <HelpOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      }
                    />
                  </Box>
                </CardContent>
              </Card>
            </Collapse>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
              sx={{
                mt: 1,
                py: 1.5,
                borderRadius: 8,
                boxShadow: 2,
                '&:hover': {
                  boxShadow: 4,
                  transform: 'translateY(-2px)',
                },
                '&:disabled': {
                  bgcolor: 'action.disabledBackground',
                },
                transition: 'all 0.3s ease',
              }}
            >
              {loading ? 'Searching...' : 'Search'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Fade>
  );
};

export default QueryForm;
